///
/// Layer

import { gsap } from 'gsap';
import { parse as queryStringParse } from 'query-string';
import { isIOS } from 'react-device-detect';
import {
    type BufferGeometry,
    Color,
    Mesh,
    Object3D,
    OrthographicCamera,
    PlaneGeometry,
    Scene,
    ShaderMaterial,
    Vector2,
    Vector3,
    type WebGLRenderer,
    WebGLRenderTarget,
} from 'three';

import type Base from '@pkg/webgl/Base';

import fragmentShader from '../shaders/copy.frag.glsl';
import vertexShader from '../shaders/copy.vert.glsl';
import { FloorGameWebGLVariant } from '../types';
import { type LayerSceneType } from '../types/layerTypes';
import LayerObserver from '../utils/LayerObserver';
import PerformanceProfile, { GPU_RANGE } from '../utils/PerformanceProfile';

interface DOMOptions {
    isScrollingElement: boolean;
    isStickyTop: boolean;
    isStickyBottom: boolean;
}

class Layer extends Object3D {
    layerId: string | number;
    geom: PlaneGeometry | BufferGeometry;
    pixelRatio: number;
    width: number;
    height: number;
    x: number;
    y: number;
    manager: LayerManager;
    zIndex: number;
    scene: LayerSceneType;
    useObserver: boolean;
    observers: unknown;
    needUpdate: boolean;
    borderRadius: number;
    isScrollingElement: boolean;
    isStickyTop: boolean;
    isStickyBottom: boolean;
    fadeBottom: boolean;
    canRender: boolean;
    isEnabled: boolean;
    needLateUpdate: boolean;
    skipPerformanceCheck: boolean;
    rt!: WebGLRenderTarget;
    layerObserver!: LayerObserver;
    domElement: HTMLElement | unknown;
    backgroundColor?: string | null;
    backgroundAlpha?: number;
    material: ShaderMaterial | unknown;
    mesh!: Mesh<BufferGeometry, ShaderMaterial>;
    layerType: FloorGameWebGLVariant | null;

    constructor(options) {
        super();
        this.layerId = options.id || 'unknown-id';
        this.geom = options.geometry ? options.geometry : options.manager.planeGeometry;
        this.pixelRatio = options.pixelRatio ? options.pixelRatio : 2;
        this.width = options.width ? options.width : 1;
        this.height = options.height ? options.height : 1;
        this.x = options.x ? options.x : 0;
        this.y = options.y ? options.y : 0;
        this.manager = options.manager;
        this.zIndex = options.zIndex ? options.zIndex : 1;
        this.scene = options.scene;
        this.useObserver = options.useObserver;
        if (this.useObserver) this.observers = options.observers;
        this.needUpdate = options.needUpdate || false;
        this.needLateUpdate = options.needLateUpdate || false;
        this.borderRadius = options.borderRadius || 0;
        this.isScrollingElement = options.isScrollingElement || false;
        this.isStickyTop = options.isStickyTop || false;
        this.isStickyBottom = options.isStickyBottom || false;
        this.fadeBottom = options.fadeBottom || false;
        this.skipPerformanceCheck = options.skipPerformanceCheck || false;
        this.backgroundColor = options.backgroundColor || null;
        this.backgroundAlpha = options.backgroundAlpha || 1.0;

        this.layerType = options.type || null;
        this.canRender = true;

        this.scene.layer = this;
        this.isEnabled = true;

        this.initBase();
        this.initMesh();
        if (this.useObserver) {
            this.initObservers();
        } else {
            this.setSize(this.width, this.height);
            this.setPosition({ left: this.x, top: this.y }, this.zIndex);
        }
    }

    initBase() {
        this.rt = new WebGLRenderTarget(this.width * this.pixelRatio, this.height * this.pixelRatio, {
            samples: PerformanceProfile.globalProfile.rtSamples || 4.0,
        });
    }

    updatePerformanceProfile() {
        if (this.skipPerformanceCheck) return;

        this.pixelRatio = PerformanceProfile.globalProfile.maxDpr;
        if (this.visible) {
            // eslint-disable-next-line no-console
            if (PerformanceProfile.debug) console.log('Update Layer DPR', this.layerId, this.pixelRatio);
            this.setSize(this.width, this.height, true);
        }
    }

    initObservers() {
        this.layerObserver = new LayerObserver({
            observers: this.observers,
            isScrollingElement: this.isScrollingElement,
            isStickyTop: this.isStickyTop,
            isStickyBottom: this.isStickyBottom,
            domElement: this.domElement || false,
            id: this.layerId,
            manager: this.manager,
        });
    }

    initMesh() {
        const material = new ShaderMaterial({
            uniforms: {
                uTexture: { value: this.rt.texture },
                uBorderRadius: { value: this.borderRadius * this.pixelRatio },
                uResolution: { value: new Vector2(this.width, this.height) },
                uAlpha: { value: 1.0 },
                uTransition: { value: 0.0 },
                uBackgroundColor: {
                    value: this.backgroundColor ? new Color(this.backgroundColor) : new Vector3(0.0, 0.0, 0.0),
                },
                uBackgroundAlpha: { value: this.backgroundAlpha || 1.0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            defines: {
                BORDER_RADIUS: this.borderRadius > 0.0,
                FLOOR_GAME_UP: this.layerType === FloorGameWebGLVariant.TierUp,
                FADE_BOTTOM: this.fadeBottom,
                USE_BACKGROUND_COLOR: !!this.backgroundColor,
            },
        });

        this.material = material;

        this.mesh = new Mesh(this.geom, material);

        this.add(this.mesh);
    }

    updateDomElement(domElement, options = {} as DOMOptions) {
        if (Object.prototype.hasOwnProperty.call(options, 'isScrollingElement')) {
            if (options.isScrollingElement !== this.isScrollingElement) {
                this.isScrollingElement = options.isScrollingElement;
                this.layerObserver.isScrollingElement = options.isScrollingElement;
            }
        }

        if (Object.prototype.hasOwnProperty.call(options, 'isStickyTop')) {
            if (options.isStickyTop !== this.isStickyTop) {
                this.isStickyTop = options.isStickyTop;
                this.layerObserver.isStickyTop = options.isStickyTop;
            }
        }

        if (Object.prototype.hasOwnProperty.call(options, 'isStickyBottom')) {
            if (options.isStickyBottom !== this.isStickyBottom) {
                this.isStickyBottom = options.isStickyBottom;
                this.layerObserver.isStickyBottom = options.isStickyBottom;
            }
        }

        this.layerObserver.observeElement(domElement, this.updateFromObserver.bind(this));

        const box = this.layerObserver.box;
        this.setSize(box.width, box.height);
        this.setPosition(box);

        // In case interaction is needed
        this.scene.setDomElement && this.scene.setDomElement(domElement);
    }

    unobserveElement() {
        this.layerObserver.unobserveElement();
    }

    updateFromObserver(box) {
        this.setSize(box.width, box.height);
        this.setPosition(box);
    }

    setSize(width, height, force = false) {
        if (this.width === Math.round(width) && this.height === Math.round(height) && !force) return;

        width = Math.max(width, 1);
        height = Math.max(height, 1);

        this.width = Math.round(width);
        this.height = Math.round(height);

        this.mesh.scale.set(this.width, this.height, 1);
        // Check Perf Profile
        this.rt.setSize(this.width * this.pixelRatio, this.height * this.pixelRatio);

        this.mesh.position.x = this.width * 0.5;
        this.mesh.position.y = -this.height * 0.5;

        this.mesh.material.uniforms.uResolution.value.x = this.width * this.pixelRatio;
        this.mesh.material.uniforms.uResolution.value.y = this.height * this.pixelRatio;

        this.mesh.material.uniforms.uBorderRadius.value = this.borderRadius * this.pixelRatio;

        this.scene.setSize && this.scene.setSize(width * this.pixelRatio, height * this.pixelRatio);
    }

    setPosition(box, zIndex = this.zIndex) {
        this.x = box.left;
        if (this.useObserver && this.isScrollingElement) this.y = box.scrollingTop;
        else this.y = box.top;
        this.zIndex = zIndex;

        this.position.set(this.x, -this.y, zIndex);
    }

    setBackgroundColor(color, alpha = 1.0) {
        this.backgroundColor = color;
        this.backgroundAlpha = alpha;
        this.mesh.material.uniforms.uBackgroundColor.value = new Color(color);
        this.mesh.material.uniforms.uBackgroundAlpha.value = alpha;

        this.mesh.material.defines.USE_BACKGROUND_COLOR = true;

        this.mesh.material.needsUpdate = true;
    }

    unsetBackgroundColor() {
        this.backgroundColor = null;
        this.backgroundAlpha = 1.0;
        this.mesh.material.defines.USE_BACKGROUND_COLOR = false;
        this.mesh.material.needsUpdate = true;
    }

    render(elapsed, delta, needScrollUpdate) {
        if (!this.scene) return;

        if (!this.isEnabled) return;

        if (this.useObserver) this.visible = this.layerObserver.isInView;

        if (!this.visible) return;

        if (this.needUpdate) {
            this.scene.update && this.scene.update(elapsed, delta);
        }

        if (!this.manager.capFPS) {
            const canScrollRender = this.manager.disableRenderingOnScroll ? !needScrollUpdate : true;
            if (this.canRender && canScrollRender) {
                this.renderFrame();
            }
        }

        this.layerObserver && this.layerObserver.update(needScrollUpdate);

        if (this.isScrollingElement && this.isStickyTop) {
            this.position.y = -Math.max(this.layerObserver.box.stickyTop - this.manager.scrollY, 0);
        } else if (this.isScrollingElement && this.isStickyBottom) {
            // if (this.manager.scrollHeight > 0.0) {
            //     console.log(
            //         `TopPosition:${this.layerObserver.box.stickyTop}`,
            //         `ScrollY:${this.manager.scrollY}`,
            //         `WindowHeight:${this.manager.height}`,
            //         `ScrollerHeight: ${this.manager.scrollHeight}`
            //     );

            //     const offsetBottom = Math.max(
            //         this.manager.scrollY + this.manager.height - this.manager.scrollHeight,
            //         0.0
            //     );
            //     this.position.y = -this.layerObserver.box.stickyTop + offsetBottom;
            // }
            if (needScrollUpdate) {
                // TODO THUNDER: Find a more performant solution
                this.layerObserver.resizeNotFrameBased();
                this.position.y = -this.layerObserver.box.top;
            }
        } else if (this.isScrollingElement) {
            this.position.y = -this.layerObserver.box.top;
        }

        // eslint-disable-next-line no-console
        if (this.manager.debugLayer) console.log('Rendered Layer', this.layerId);
    }

    lateRender() {
        if (!this.needLateUpdate) return;
        if (!this.visible) return;

        this.scene.lateUpdate && this.scene.lateUpdate();
    }

    renderFrame() {
        this.manager.renderer.setRenderTarget(this.rt);
        this.manager.renderer.render(this.scene.scene, this.scene.camera);
        this.manager.renderer.setRenderTarget(null);
    }

    enable() {
        this.isEnabled = true;
        if (!this.useObserver) this.visible = true;
    }

    disable() {
        this.isEnabled = false;
        this.visible = false;
    }

    delete() {
        this.disable();
        this.layerObserver && this.layerObserver.delete();
        this.rt.dispose();
        this.mesh.material.dispose();
        this.mesh.geometry.dispose();

        this.scene.delete && this.scene.delete();
    }

    animateIn(type = 'fade-in') {
        if (type === 'fade-in') {
            gsap.fromTo(
                this.mesh.material.uniforms.uAlpha,
                {
                    value: 0,
                },
                {
                    value: 1.0,
                    duration: 0.6,
                    ease: 'power2.out',
                },
            );
        } else {
            this.mesh.material.uniforms.uAlpha.value = 1.0;
        }
    }

    async animateOut(type = 'fade-out') {
        if (type === 'fade-out') {
            await gsap.to(this.mesh.material.uniforms.uAlpha, {
                value: 0,
                duration: 1.0,
                ease: 'power2.out',
            });
        }
    }
}

///
/// Layer Manager

export class LayerManager {
    width: number;
    height: number;
    camera!: OrthographicCamera;
    layers: object;
    scrollY: number;
    scrollHeight: number;
    bodyScrollHeight: number;
    renderer: WebGLRenderer;
    debugLayer: boolean;
    world: Base;
    canvas: HTMLCanvasElement;
    needScrollUpdate: boolean;
    needsResize: boolean;
    needsScroll: boolean;
    needsScrollHeightResize: boolean;
    scene!: Scene;
    disableRenderingOnScroll: boolean;
    planeGeometry!: PlaneGeometry;
    observers!: {
        mutation: MutationObserver;
        mutationConfig: object;
        mutationMap: Map<HTMLElement, () => void>;

        resize: ResizeObserver;
        resizeConfig: object;
        resizeMap: Map<HTMLElement, () => void>;

        intersection: IntersectionObserver;
        intersectionMap: Map<HTMLElement, () => void>;
    };
    skipNextFrame: boolean | undefined;
    useElementScroller: boolean;
    targetScrollY!: number;
    elapsed!: number;
    delta!: number;
    needRender: boolean;
    canvasVisible: boolean;
    capFPS: boolean;
    navigationElement!: HTMLElement;
    pageDomObserver!: ResizeObserver;

    constructor(world) {
        this.world = world;
        this.renderer = world.renderer;

        this.canvas = this.renderer.domElement;

        this.scrollY = 0.0;
        this.scrollHeight = 0.0;
        this.bodyScrollHeight = 0.0;

        this.needScrollUpdate = false;
        this.needsResize = true;
        this.needsScroll = true;
        this.needsScrollHeightResize = false;

        this.capFPS = false;

        this.width = window.innerWidth;
        this.height = window.innerHeight;

        this.skipNextFrame = false;
        this.useElementScroller = false;
        this.canvasVisible = true;

        this.needRender = true;
        // this.disableRenderingOnScroll = PerformanceProfile.range < GPU_RANGE.MEDIUM;
        this.disableRenderingOnScroll = false;

        this.layers = {};

        this.debugLayer = Object.prototype.hasOwnProperty.call(queryStringParse(location.search), 'debugLayers');

        this.initBase();
        this.initCamera();
        this.initEvents();
        this.initObservers();

        PerformanceProfile.onLayerManagerUpdateProfile = this.updatePerformanceProfile.bind(this);
    }

    initBase() {
        this.scene = new Scene();
        this.planeGeometry = new PlaneGeometry(1, 1);
    }

    initCamera() {
        this.camera = new OrthographicCamera(
            this.width / -2,
            this.width / 2,
            this.height / 2,
            this.height / -2,
            0.01,
            11,
        );
        this.camera.position.z = 10;
        this.scene.position.x = -this.width * 0.5;
        this.scene.position.y = this.height * 0.5;
    }

    initEvents() {
        window.addEventListener('resize', this.resize.bind(this));
        window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

        this.pageDomObserver = new ResizeObserver(() => {
            Object.entries(this.layers).forEach(([, layer]) => {
                if (layer.useObserver) layer.layerObserver.resize();
            });
        });

        if (isIOS) {
            this.resize();
        }

        // If <main> height get updated, scrollHeight did changed
        const main = document.querySelector('main');
        if (main instanceof HTMLElement) {
            this.pageDomObserver.observe(main);
        }
    }

    observeNavigation(element) {
        if (this.navigationElement) this.pageDomObserver.unobserve(this.navigationElement);
        if (!element) return;
        this.navigationElement = element;
        this.pageDomObserver.observe(this.navigationElement);
    }

    onScroll() {
        this.needScrollUpdate = false;
        // TODO Find a way to avoid looping
        Object.entries(this.layers).forEach(([, layer]) => {
            if (layer.visible && layer.isScrollingElement) {
                this.needScrollUpdate = true;
            }
        });
    }

    addLayer({
        id,
        scene,
        zIndex,
        pixelRatio = 1,
        width = 1,
        height = 1,
        x = 0,
        y = 0,
        domElement = false,
        useObserver = true,
        isScrollingElement = true,
        needUpdate = false,
        needLateUpdate = false,
        borderRadius = 0,
        fadeBottom = false,
        skipPerformanceCheck = false,
        type = null,
    }) {
        this.layers[id] = new Layer({
            id: id,
            zIndex: zIndex,
            geometry: this.planeGeometry,
            manager: this,
            pixelRatio,
            width,
            height,
            x,
            y,
            domElement: domElement,
            scene: scene,
            useObserver: useObserver,
            observers: this.observers,
            isScrollingElement: isScrollingElement,
            needUpdate,
            needLateUpdate,
            borderRadius,
            skipPerformanceCheck,
            fadeBottom,
            type,
        });

        this.scene.add(this.layers[id]);

        // eslint-disable-next-line no-console
        if (this.debugLayer) console.log('Added layer', id);
    }

    updateLayer(id, domElement, options = {}) {
        this.layers[id].updateDomElement(domElement, options);
        this.layers[id].enable();

        // eslint-disable-next-line no-console
        if (this.debugLayer) console.log('Updated layer:', id);
    }

    enableLayer(id) {
        this.layers[id].enable();
    }

    disableLayer(id) {
        this.layers[id].disable();
    }

    deleteLayer(id) {
        if (this.layers[id]) {
            this.scene.remove(this.layers[id]);
            this.layers[id].delete();
            delete this.layers[id];

            // eslint-disable-next-line no-console
            if (this.debugLayer) console.log('Deleted layer:', id);
        }
    }

    resize() {
        if (!this.needsResize) return;

        this.needsResize = false;

        requestAnimationFrame(() => {
            this.needsResize = true;

            let height, width;

            if (isIOS) {
                const element = this.renderer.domElement.parentNode as HTMLElement;
                height = element.clientHeight;
                width = element.clientWidth;

                if (width === 0 || height === 0) {
                    setTimeout(() => {
                        this.resize();
                    }, 100);
                    return;
                }
            } else {
                height = window.innerHeight;
                width = window.innerWidth;
            }

            if (width === this.width && height === this.height && !this.needsScrollHeightResize) return;

            this.needsScrollHeightResize = false;

            this.height = height;
            this.width = width;

            this.camera.left = this.width / -2;
            this.camera.right = this.width / 2;
            this.camera.top = this.height / 2;
            this.camera.bottom = this.height / -2;
            this.camera.updateProjectionMatrix();

            this.scene.position.x = -this.width * 0.5;
            this.scene.position.y = this.height * 0.5;

            this.renderer.setSize(this.width, this.height);

            Object.entries(this.layers).forEach(([, layer]) => {
                if (layer.useObserver) layer.layerObserver.resize();
            });

            // this.render(this.elapsed, this.delta);
            this.skipNextFrame = true;
        });
    }

    // Check src/components/Prototype/Scrollbar/index.tsx for reference
    setScrollFromElement(scrollY, scrollHeight) {
        // console.log('set scroll from element');
        this.useElementScroller = true;
        this.targetScrollY = scrollY;
        this.scrollHeight = scrollHeight;
    }

    unsetScrollFromElement() {
        this.useElementScroller = false;
        this.scrollHeight = 0;
    }

    getScroll() {
        if (this.useElementScroller) return this.targetScrollY;
        else return window.scrollY;
    }

    initObservers() {
        const resizeMap = new Map();
        const resizeConfig = { box: 'border-box' };
        const resizeObserver = new ResizeObserver((resizeList) => {
            resizeList.forEach((mutation) => {
                const callback = resizeMap.get(mutation.target);
                callback && callback(true);
            });
        });

        const mutationMap = new Map();
        const mutationConfig = {
            attributes: true,
            attributeFilter: ['class', 'style'],
            childList: true,
            subtree: false,
        };
        const mutationObserver = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                const callback = mutationMap.get(mutation.target);
                callback && callback(true);
            });
        });

        const intersectionMap = new Map();
        const intersectionObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const callback = intersectionMap.get(entry.target);
                callback && callback(entry.isIntersecting);
            }
        });

        this.observers = {
            mutation: mutationObserver,
            mutationConfig: mutationConfig,
            mutationMap: mutationMap,

            resize: resizeObserver,
            resizeConfig: resizeConfig,
            resizeMap: resizeMap,

            intersection: intersectionObserver,
            intersectionMap: intersectionMap,
        };
    }

    showCanvas() {
        this.canvasVisible = true;
        this.canvas.style.display = 'block';
    }

    hideCanvas() {
        this.canvasVisible = false;
        this.canvas.style.display = 'none';
    }

    updatePerformanceProfile() {
        // Cap 30fps instead of disabling scroll
        this.disableRenderingOnScroll = false;

        Object.entries(this.layers).forEach(([, layer], index) => {
            setTimeout(() => {
                layer.updatePerformanceProfile();
            }, 20 * index);
        });
        this.skipNextFrame = true;
    }

    render(elapsed, delta) {
        this.elapsed = elapsed;
        this.delta = delta;

        if (this.skipNextFrame) {
            this.skipNextFrame = false;
            return;
        }
        let needsUpdateScroll = false;

        if (this.useElementScroller) {
            needsUpdateScroll = this.scrollY !== this.targetScrollY;
            if (needsUpdateScroll) this.scrollY = this.targetScrollY;
        } else if (this.needScrollUpdate) {
            const scrollY = window.scrollY;
            needsUpdateScroll = this.scrollY !== scrollY;
            if (needsUpdateScroll) {
                this.scrollY = scrollY;
                // Alernative Solution to listening to resizwe on <main>. TBD if needed after testing
                // const bodyScrollHeight = document.body.scrollHeight;
                // if (this.bodyScrollHeight !== bodyScrollHeight) {
                //     this.bodyScrollHeight = bodyScrollHeight;
                //     this.needsScrollHeightResize = true;
                //     this.resize();
                // }
            }

            this.needScrollUpdate = false;
        }

        if (isIOS && needsUpdateScroll) {
            if (this.useElementScroller) this.canvas.style.transform = `translate3d(0, 0px, 0)`;
            else this.canvas.style.transform = `translate3d(0, ${this.scrollY}px, 0)`;
        }

        this.needRender = false;

        // Limit FPS on Low Perf Profile
        if (PerformanceProfile.range < GPU_RANGE.LOW) {
            // Render one frame out of two (30fps)
            this.capFPS = !this.capFPS;
        } else if (this.capFPS) {
            this.capFPS = false;
        }

        Object.entries(this.layers).forEach(([, layer]) => {
            layer.render(elapsed, delta, needsUpdateScroll);
            if (layer.visible) {
                this.needRender = true;
            }
        });

        if (!this.needRender && this.canvasVisible) this.hideCanvas();
        else if (this.needRender && !this.canvasVisible) this.showCanvas();

        if (this.needRender) {
            this.renderer.render(this.scene, this.camera);

            Object.entries(this.layers).forEach(([, layer]) => {
                layer.lateRender();
            });
        }
    }
}
